import {
  ExternalIntegration,
  ExternalIntegrationEnum,
  GoHighLevelIntegration,
  MailchimpIntegration,
} from 'api/clients/ExternalIntegrationsClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { getPropsFromIntegration } from './utils'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { ReactNode } from 'react'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useDeleteExternalIntegration } from 'hooks/queries/externalIntegrations/useDeleteExternalIntegration'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useModals } from 'almanac-ui/Modal'
import { MailchimpSettingsModal } from '../components/MailchimpSettingsModal/MailchimpSettingsModal'
import { GoHighLevelConfigurationModal } from 'components/GoHighLevelConfigurationModal/GoHighLevelConfigurationModal'

export interface ConnectedAccountCardProps {
  avatarUrl?: string
  icon: ReactNode
  details?: string
  name?: string
  onDisconnectAccount: () => void
  onRefreshConnection: (options?: { popup?: boolean }) => void
  isDeleting: boolean
  onConfigureClick: () => void
  showSettings: boolean
}

export const useConnectedAccountCardProps = (integration: ExternalIntegration): ConnectedAccountCardProps => {
  const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
  const showSettings =
    (goHighLevelEnabled && integration.provider === ExternalIntegrationEnum.GoHighLevel) ||
    integration.provider === ExternalIntegrationEnum.Mailchimp

  const { openModal } = useModals()

  const onConfigureClick = () => {
    switch (integration.provider) {
      case ExternalIntegrationEnum.Mailchimp:
        return openModal(MailchimpSettingsModal, { integration: integration as MailchimpIntegration })
      case ExternalIntegrationEnum.GoHighLevel:
        return openModal(GoHighLevelConfigurationModal, { integration: integration as GoHighLevelIntegration })
    }
  }

  const { mutateAsync: onDisconnectAccount, isLoading: isDeleting } = useDeleteExternalIntegration(integration.id, {
    onSuccess: () => {
      showToastMessage(`Successfully disconnected ${ProviderToName[integration.provider]} account!`, 'success')
    },
    onError: () => {
      showToastMessage(`Unable to disconnect ${ProviderToName[integration.provider]} account`, 'error')
    },
  })

  const { initiateOauth } = useInitiateOauth()
  const { connect } = useConnectAccountPopup()

  const onRefreshConnection = async (options?: { popup?: boolean }) => {
    const { popup } = options || {}

    if (popup) {
      const popup = await initiateOauth(
        integration.provider,
        integration.id as number,
        '_blank',
        'popup=true,width=600,height=780',
      )
      if (popup) {
        connect(popup, {
          onIntegrationAuthCreated: () => {
            showToastMessage('Connection refreshed successfully!', 'success')
          },
        })
      }
    } else {
      initiateOauth(integration.provider, integration.id as number)
    }
  }

  return {
    ...getPropsFromIntegration(integration),
    onDisconnectAccount,
    onRefreshConnection,
    isDeleting,
    onConfigureClick,
    showSettings,
  }
}
